import React from 'react'
import type { VelosterHealthStatus } from '@/services/velosterHealthService'
import { cn } from '@/utils/helpers'

const LEVEL_DOT: Record<VelosterHealthStatus['level'], string> = {
  operational: 'bg-status-ok',
  degraded: 'bg-status-warn',
  outage: 'bg-status-danger',
  scheduled_off: 'bg-muted',
  unknown: 'bg-subtle',
}

const LEVEL_LABEL: Record<VelosterHealthStatus['level'], string> = {
  operational: 'Operacional',
  degraded: 'Instável',
  outage: 'Indisponível',
  scheduled_off: 'Desligado (programado)',
  unknown: 'Desconhecido',
}

interface Props {
  veloster: VelosterHealthStatus | null
  className?: string
}

const VelosterHealthPanel: React.FC<Props> = ({ veloster, className }) => {
  if (!veloster) return <p className={cn('text-xs text-muted', className)}>Sem dados de telemetria.</p>

  return (
    <div className={className}>
      <div className="flex items-center gap-1.5 mb-2">
        <span className={cn('w-2 h-2 rounded-full inline-block', LEVEL_DOT[veloster.level])} />
        <span className="text-sm font-medium text-body">{LEVEL_LABEL[veloster.level]}</span>
        {veloster.failureRate > 0 && (
          <span className="text-[11px] text-muted">
            · falhas {Math.round(veloster.failureRate * 100)}%
          </span>
        )}
      </div>
      {veloster.gpus.length > 0 ? (
        <ul className="space-y-1.5">
          {veloster.gpus.map((gpu) => (
            <li
              key={gpu.id}
              className="rounded border border-border-subtle bg-surface-muted px-2 py-1.5"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-body flex items-center gap-1.5">
                  <span
                    className={cn(
                      'w-1.5 h-1.5 rounded-full inline-block',
                      gpu.ready ? 'bg-status-ok' : 'bg-status-danger',
                    )}
                  />
                  GPU {gpu.id}
                </span>
                <span className="text-[11px] text-muted">{gpu.ready ? 'pronta' : 'indisponível'}</span>
              </div>
              <div className="mt-1 grid grid-cols-3 gap-1 text-[11px] text-muted">
                <span>{gpu.temp_c != null ? `${gpu.temp_c}°C` : '—'}</span>
                <span>{gpu.util_pct != null ? `${gpu.util_pct}%` : '—'}</span>
                <span>
                  {gpu.mem_used_mb != null && gpu.mem_total_mb != null
                    ? `${gpu.mem_used_mb}/${gpu.mem_total_mb} MB`
                    : '—'}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted">Nenhuma GPU reportada.</p>
      )}
    </div>
  )
}

export default VelosterHealthPanel
